import React, { useMemo } from "react";
import Select, { MultiValue, components, OptionProps } from "react-select";
import styles from "./MultiSelect.module.css";
import { SelectOption } from "../types";

interface MultiSelectProps {
    label?: string;
    placeholder?: string;
    options: SelectOption[];
    value: string[];
    onChange: (next: string[]) => void;
    disabled?: boolean;
    loading?: boolean;
    icon?: React.ReactNode;
}

const styleOverrides = {
    control: (base: any) => ({
        ...base,
        borderRadius: 11,
        minHeight: 42,
        fontSize: 13,
        borderColor: "var(--analytics-border, #e1e8ea)",
        boxShadow: "none",
        "&:hover": { borderColor: "var(--analytics-primary, var(--green-600))" },
    }),
    valueContainer: (base: any) => ({
        ...base,
        padding: "4px 8px",
        gap: 4,
    }),
    multiValue: (base: any) => ({
        ...base,
        backgroundColor: "rgba(134, 227, 206, 0.12)",
        borderRadius: 999,
        padding: "0 4px",
    }),
    multiValueLabel: (base: any) => ({
        ...base,
        color: "var(--analytics-text, #0f2a2e)",
        fontSize: 12,
    }),
    multiValueRemove: (base: any) => ({
        ...base,
        color: "var(--analytics-text-muted, #6b7e84)",
        ":hover": {
            backgroundColor: "rgba(134, 227, 206, 0.25)",
            color: "var(--analytics-text, #0f2a2e)",
        },
    }),
    placeholder: (base: any) => ({
        ...base,
        color: "var(--analytics-text-muted, #6b7e84)",
        fontSize: 13,
    }),
    menu: (base: any) => ({
        ...base,
        zIndex: 11000,
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0 8px 24px rgba(15, 42, 46, 0.12)",
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 11000 }),
    option: (base: any, state: any) => ({
        ...base,
        fontSize: 13,
        backgroundColor: state.isFocused ? "rgba(134, 227, 206, 0.08)" : "#ffffff",
        color: "var(--analytics-text, #0f2a2e)",
        cursor: "pointer",
    }),
};

const Option = (props: OptionProps<SelectOption, true>) => (
    <components.Option {...props}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span>{props.data.label}</span>
            {props.data.description ? (
                <span style={{ fontSize: 11, color: "var(--analytics-text-muted, #6b7e84)" }}>
                    {props.data.description}
                </span>
            ) : null}
        </div>
    </components.Option>
);

const MultiSelect: React.FC<MultiSelectProps> = ({
    label,
    placeholder,
    options,
    value,
    onChange,
    disabled = false,
    loading = false,
    icon,
}) => {
    const selectedOptions = useMemo<SelectOption[]>(
        () => options.filter((o) => value.includes(o.value)),
        [options, value],
    );

    const handleChange = (next: MultiValue<SelectOption>) => {
        onChange(next.map((o) => o.value));
    };

    return (
        <div className={styles.root}>
            {label ? <label className={styles.label}>{label}</label> : null}
            <Select
                isMulti
                options={options}
                value={selectedOptions}
                onChange={handleChange}
                placeholder={
                    icon ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            {icon}
                            {placeholder}
                        </span>
                    ) : (
                        placeholder
                    )
                }
                isDisabled={disabled}
                isLoading={loading}
                closeMenuOnSelect={false}
                styles={styleOverrides as any}
                components={{ Option }}
                menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                menuPosition="fixed"
            />
        </div>
    );
};

export default MultiSelect;
